import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Bookmark, Award, Calendar } from 'lucide-react';

const DashboardPage = () => {
  const { currentUser } = useAuth();
  const { pathways, pathwayProgress, courses, courseProgress, savedPacks, events } = useData();
  const navigate = useNavigate();

  // Get user's active pathway
  const activePathway = pathways.find(p => {
    const progress = pathwayProgress[p.id];
    return progress && progress.completedSteps.length > 0 && progress.completedSteps.length < p.steps.length;
  });

  const activePathwayProgress = activePathway ? pathwayProgress[activePathway.id] : null;
  const activePathwayPercent = activePathway
    ? Math.round((activePathwayProgress.completedSteps.length / activePathway.steps.length) * 100)
    : 0;

  // Get recommended next actions
  const nextStep = activePathway?.steps.find(step => !activePathwayProgress?.completedSteps.includes(step.id));

  // Get courses in progress
  const coursesInProgress = courses.filter(c => {
    const progress = courseProgress[c.id];
    return progress && progress.completedLessons.length > 0;
  });

  const userCredentials = [
    { label: 'BESS 101', icon: '✓' },
    { label: 'Sizing tool', icon: '✓' },
    { label: '6 contributions', icon: '⚡' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <div className="mb-8">
          <span className="eyebrow">My GridCommons</span>
          <h2 className="mt-3 mb-2">Welcome back, {currentUser?.name?.split(' ')[0]}</h2>
          <p style={{ color: 'hsl(var(--ink-muted))' }}>Here's where you left off — assembled for you by Grid Guide.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Continue Pathway - Full Width */}
          {activePathway && (
            <Card className="md:col-span-2 p-6">
              <span className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Continue your pathway</span>
              <h3 className="mt-2 mb-4">{activePathway.title}</h3>
              <div className="w-full h-2 rounded-full mb-2" style={{ backgroundColor: 'hsl(var(--line-2))' }}>
                <div className="h-full rounded-full" style={{ width: `${activePathwayPercent}%`, backgroundColor: 'hsl(var(--mint))' }} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>
                  {activePathwayPercent}% · next: {nextStep?.title}
                </span>
                <Button size="sm" onClick={() => navigate(`/pathways/${activePathway.slug}`)}>
                  Resume <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Card>
          )}

          {!activePathway && (
            <Card className="md:col-span-2 p-6">
              <h3 className="mb-2">Start a new pathway</h3>
              <p className="text-sm mb-4" style={{ color: 'hsl(var(--ink-muted))' }}>Choose a goal-based journey to begin.</p>
              <Button onClick={() => navigate('/')}>Explore pathways</Button>
            </Card>
          )}

          {/* Recommended Next */}
          <Card className="p-5">
            <h4 className="text-sm font-semibold mb-4">✦ Recommended next</h4>
            <div className="space-y-3">
              <button
                className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:shadow-md"
                style={{ backgroundColor: 'hsl(var(--surface-2))' }}
                onClick={() => navigate('/community')}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))', fontWeight: '700' }}>
                  LM
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">Connect with Lerato Mokoena</div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>Matched to your pathway</div>
                </div>
              </button>

              {coursesInProgress.length === 0 && (
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:shadow-md"
                  style={{ backgroundColor: 'hsl(var(--surface-2))' }}
                  onClick={() => navigate('/academy')}
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded flex-shrink-0" style={{ backgroundColor: 'hsl(var(--violet-soft))' }}>
                    🎓
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">Start: Storage Procurement</div>
                    <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>3.5 hrs · 0% done</div>
                  </div>
                </button>
              )}
            </div>
          </Card>

          {/* Saved Packs */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold">Saved packs</h4>
              <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge')}>All <ArrowRight className="ml-1 h-3 w-3" /></Button>
            </div>
            {savedPacks.length > 0 ? (
              <div className="space-y-2">
                {savedPacks.slice(0, 2).map(pack => (
                  <button
                    key={pack.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:shadow-md"
                    style={{ backgroundColor: 'hsl(var(--surface-2))' }}
                    onClick={() => navigate('/knowledge')}
                  >
                    <Bookmark className="h-5 w-5 flex-shrink-0" style={{ color: 'hsl(var(--accent-ink))' }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{pack.title}</div>
                      <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>{pack.items.length} resources</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>No saved packs yet.</p>
            )}
          </Card>

          {/* Credentials */}
          <Card className="p-5">
            <h4 className="text-sm font-semibold mb-4">Your credentials</h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {userCredentials.map(cred => (
                <span key={cred.label} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>
                  {cred.icon} {cred.label}
                </span>
              ))}
            </div>
            {currentUser?.isCoEAlumni && (
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
                <Award className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
                <div>
                  <div className="text-sm font-medium">CoE Alumni · 2025</div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>Visible on your profile</div>
                </div>
              </div>
            )}
          </Card>

          {/* Upcoming Events */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold">Upcoming</h4>
              <Button variant="ghost" size="sm" onClick={() => navigate('/academy')}>Calendar <ArrowRight className="ml-1 h-3 w-3" /></Button>
            </div>
            <div className="space-y-3">
              {events.slice(0, 2).map(event => (
                <div key={event.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
                  <Calendar className="h-5 w-5 flex-shrink-0" style={{ color: 'hsl(var(--primary))' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{event.title}</div>
                    <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>
                      {new Date(event.startsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {event.location}
                    </div>
                  </div>
                  {event.id === 'event-1' && (
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'hsl(var(--mint))', color: 'hsl(var(--ink))' }}>Registered</span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default DashboardPage;
